import * as React from "react";
import { StringHelper } from "../../assets/utils/helpers";
import { Constants } from "../../assets/utils/lib/constants";


export const VIEW_IMAGE_ACTION = "view-image";

type QueryValue = string | number | boolean | null | undefined;

const buildUrl = (
    baseUrl: string,
    routeSegments: string[],
    query?: Record<string, QueryValue>
): string => {
    const base: string = StringHelper.enforceEndsWith(baseUrl, "/");
    const route: string = routeSegments.join("/");

    const queryString: string | undefined =
        query &&
        Object.entries(query)
            .filter(([, queryValue]) => queryValue !== undefined && queryValue !== null && queryValue !== "")
            .map(([key, queryValue]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(queryValue))}`)
            .join("&");

    return queryString ? `${base}${route}?${queryString}` : `${base}${route}`;
};

export const buildViewImageUrl = (
    hostUrl: string,
    imagePath: string | undefined | null,
    routeSegments: string[] = ["pophub", "mobile", "Device", "ViewImage"]
): string => {
    if (!imagePath) return Constants.emptyString;

    return buildUrl(hostUrl, routeSegments, { imagePath });
};

export function useViewImageAction<TRow>(props: {
    rows: TRow[];
    hostUrl: string;
    getId: (row: TRow) => string | undefined | null;
    getImagePath: (row: TRow) => string | undefined | null;
    routeSegments?: string[];
}) {
    const [imageViewer, setImageViewer] = React.useState<{
        isOpen: boolean;
        url?: string;
    }>({ isOpen: false });

    const imagePathById: Record<string, string> = React.useMemo(() => {
        return props.rows.reduce<Record<string, string>>((acc, currentRow) => {
            const id: string | null | undefined = props.getId(currentRow);
            const path: string | null | undefined = props.getImagePath(currentRow);

            if (id && path) acc[String(id)] = path;
            return acc;
        }, {});
    }, [props.rows, props.getId, props.getImagePath]);

    const handleViewImageAction = React.useCallback(
        (actionType: string, dataId: string): boolean => {
            if (actionType !== VIEW_IMAGE_ACTION) return false;

            const path: string = imagePathById[dataId];
            if (!path) return true;

            setImageViewer({
                isOpen: true,
                url: buildViewImageUrl(props.hostUrl, path, props.routeSegments),
            });

            return true;
        },
        [imagePathById, props.hostUrl, props.routeSegments]
    );

    const closeImage = React.useCallback(() => {
        setImageViewer({ isOpen: false });
    }, []);

    return { imageViewer, closeImage, handleViewImageAction };
}

